import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Check, Warning, Spinner, ArrowClockwise } from '@phosphor-icons/react';
import api from '../lib/api';
import { addAccessedCode } from '../lib/tripHistory';

export default function PaymentReturn() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const orderId = params.get('order_id') || '';
  const draftId = params.get('draft_id') || '';
  const [status, setStatus] = useState('polling'); // polling | confirmed | failed | timeout
  const [resolvedDraftId, setResolvedDraftId] = useState(draftId);
  const pollCount = useRef(0);
  const intervalRef = useRef(null);

  const savedCodeRef = useRef(false);
  const saveTripCode = (syncResult) => {
    if (savedCodeRef.current) return;
    const code = syncResult?.trip_code || syncResult?.activation?.code;
    if (code) {
      addAccessedCode(code);
      savedCodeRef.current = true;
    }
  };

  const doSync = async (oid) => {
    if (!oid) return null;
    try {
      const r = await api.post(`/organiser/payments/by-order/${oid}/sync`);
      return r.data;
    } catch { return null; }
  };

  const doPublicSync = async () => {
    try {
      const body = orderId ? { order_id: orderId } : draftId ? { draft_id: draftId } : null;
      if (!body) return null;
      const r = await api.post('/payments/public-sync', body);
      return r.data;
    } catch { return null; }
  };

  useEffect(() => {
    let cancelled = false;

    const startPolling = async () => {
      // Try authenticated sync first if we have order_id
      let oid = orderId;
      let syncResult = null;

      if (oid) {
        syncResult = await doSync(oid);
        if (cancelled) return;
      }

      // Fallback: public-sync (works without JWT, accepts draft_id or order_id)
      if (!syncResult || syncResult.status === undefined) {
        syncResult = await doPublicSync();
        if (cancelled) return;
      }

      if (syncResult?.status === 'confirmed') {
        saveTripCode(syncResult);
        setStatus('confirmed');
        return;
      }
      if (syncResult?.status === 'failed') {
        setStatus('failed');
        return;
      }

      // Start polling via public-sync
      intervalRef.current = setInterval(async () => {
        pollCount.current += 1;
        if (pollCount.current > 30) {
          clearInterval(intervalRef.current);
          setStatus('timeout');
          return;
        }
        try {
          const r = await doPublicSync();
          if (r?.status === 'confirmed') {
            clearInterval(intervalRef.current);
            saveTripCode(r);
            setStatus('confirmed');
          } else if (r?.status === 'failed') {
            clearInterval(intervalRef.current);
            setStatus('failed');
          }
        } catch {}
      }, 15000);
    };

    startPolling();
    return () => { cancelled = true; if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [orderId, draftId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleManualSync = async () => {
    const result = await doPublicSync();
    if (result?.status === 'confirmed') { saveTripCode(result); setStatus('confirmed'); }
    else if (result?.status === 'failed') setStatus('failed');
  };

  if (status === 'polling') {
    return (
      <div className="min-h-screen bg-[#F4F6F5] flex items-center justify-center px-4" data-testid="payment-return">
        <Card className="w-full max-w-md p-8 bg-white border-[#E1E6E4] rounded-xl text-center space-y-4">
          <div className="w-14 h-14 bg-[#E3F2FD] rounded-full flex items-center justify-center mx-auto animate-pulse">
            <Spinner className="w-7 h-7 text-[#1565C0] animate-spin" weight="bold" />
          </div>
          <h2 className="text-xl font-bold text-[#1D242B]">Processing payment...</h2>
          <p className="text-sm text-[#455561]">Please wait while we confirm your payment with Revolut.</p>
          {orderId && (
            <Button onClick={handleManualSync} variant="outline" size="sm" className="border-[#E1E6E4] text-[#455561] rounded-lg" data-testid="manual-refresh">
              <ArrowClockwise className="w-4 h-4 mr-1" weight="bold" />Refresh
            </Button>
          )}
        </Card>
      </div>
    );
  }

  if (status === 'confirmed') {
    return (
      <div className="min-h-screen bg-[#F4F6F5] flex items-center justify-center px-4" data-testid="payment-return">
        <Card className="w-full max-w-md p-8 bg-white border-[#E1E6E4] rounded-xl text-center space-y-4">
          <div className="w-14 h-14 bg-[#E8F5E9] rounded-full flex items-center justify-center mx-auto">
            <Check className="w-7 h-7 text-[#2E7D32]" weight="bold" />
          </div>
          <h2 className="text-xl font-bold text-[#1D242B]">Payment confirmed!</h2>
          <p className="text-sm text-[#455561]">Your event is now live. Check your email for the event code and credentials.</p>
          <p className="text-xs text-[#B8C7C0] mt-1">Can't see it? Check your Junk or Spam folder, and mark <strong>noreply@foretour.co.uk</strong> as safe.</p>
          <Button onClick={() => navigate('/account')} className="bg-[#1B4332] text-white rounded-xl font-semibold px-6" data-testid="payment-goto-account">My Account</Button>
        </Card>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="min-h-screen bg-[#F4F6F5] flex items-center justify-center px-4" data-testid="payment-return">
        <Card className="w-full max-w-md p-8 bg-white border-[#E1E6E4] rounded-xl text-center space-y-4">
          <div className="w-14 h-14 bg-[#FFEBEE] rounded-full flex items-center justify-center mx-auto">
            <Warning className="w-7 h-7 text-[#C62828]" weight="bold" />
          </div>
          <h2 className="text-xl font-bold text-[#1D242B]">Payment failed</h2>
          <p className="text-sm text-[#455561]">Your payment was not successful. Please try again from your drafts.</p>
          <Button onClick={() => navigate(resolvedDraftId ? `/wizard/new?step=summary&draft_id=${resolvedDraftId}` : '/account')} className="bg-[#1B4332] text-white rounded-xl font-semibold px-6" data-testid="payment-retry">
            Try again
          </Button>
          <Button onClick={() => navigate('/account')} variant="outline" className="w-full rounded-xl border-[#E1E6E4] text-[#455561] font-semibold" data-testid="payment-goto-account-fail">My Account</Button>
        </Card>
      </div>
    );
  }

  // timeout
  return (
    <div className="min-h-screen bg-[#F4F6F5] flex items-center justify-center px-4" data-testid="payment-return">
      <Card className="w-full max-w-md p-8 bg-white border-[#E1E6E4] rounded-xl text-center space-y-4">
        <div className="w-14 h-14 bg-[#FFF3E0] rounded-full flex items-center justify-center mx-auto">
          <Warning className="w-7 h-7 text-[#E59000]" weight="bold" />
        </div>
        <h2 className="text-xl font-bold text-[#1D242B]">Still processing</h2>
        <p className="text-sm text-[#455561]">Your payment is still being processed. We'll email you when it's confirmed.</p>
        <Button onClick={() => navigate('/account')} className="bg-[#1B4332] text-white rounded-xl font-semibold px-6" data-testid="payment-goto-account-timeout">My Account</Button>
      </Card>
    </div>
  );
}
